import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { DatabaseIcon } from '@/pages/TableViews/utils/databaseSources';
import {
  useAddDatabaseItemsService,
  useDatabaseQueriesService,
  useDatabaseSourcesService,
  useGetTemplatesService,
  useSetDatabaseActiveService,
  useTableViewService,
  useTestDatabaseQueryService,
} from '@/services/tableViews';
import type { DatabaseSource, DatabaseSourceInfo } from '@ditto/shared';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Database,
  Loader2,
  Plus,
  Search,
  Star,
} from 'lucide-react';
import { useEffect, useMemo, useState, type FC } from 'react';
import { toast } from 'sonner';
import { ConfirmationModal } from './components/ConfirmationModal';
import { getDatabaseInputComponent } from './components/database-inputs';

interface DatabaseCardProps {
  source: DatabaseSourceInfo;
  tableViewId: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  initialQuery?: string;
  initialResultCount?: number | null;
  isRecommended?: boolean;
  isActive: boolean;
  doesTableHaveRows: boolean;
  numItems: number;
}

const DatabaseCard: FC<DatabaseCardProps> = ({
  source,
  tableViewId,
  isCollapsed = false,
  onToggleCollapse,
  initialQuery = '',
  initialResultCount = null,
  isRecommended = false,
  isActive: _isActive,
  doesTableHaveRows,
  numItems,
}) => {
  const [currentQuery, setCurrentQuery] = useState('');
  const [isQueryValid, setIsQueryValid] = useState(false);
  const [resultCount, setResultCount] = useState<number | null>(
    initialResultCount
  );
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  useEffect(() => {
    setResultCount(initialResultCount);
  }, [initialResultCount]);

  const { testQuery, isTesting } = useTestDatabaseQueryService();
  const { addDatabaseItems, isAdding } = useAddDatabaseItemsService();
  const { setDatabaseActive, isUpdating } = useSetDatabaseActiveService();

  const handleToggleCollapse = async () => {
    if (!isUpdating) {
      try {
        const newActiveState = isCollapsed;

        await setDatabaseActive({
          tableViewId,
          data: {
            source: source.id as DatabaseSource,
            isActive: newActiveState,
          },
        });

        // Call parent callback to update UI
        onToggleCollapse?.();
      } catch (error) {
        toast.error('Failed to update database state');
      }
    }
  };

  const handleQueryChange = (query: string, isValid: boolean) => {
    setCurrentQuery(query);
    setIsQueryValid(isValid);
  };

  const handleTest = async () => {
    if (!isQueryValid) {
      setShowErrors(true);
      return;
    }
    setResultCount(null);
    try {
      const result = await testQuery({
        tableViewId,
        data: {
          source: source.id as DatabaseSource,
          query: currentQuery,
        },
      });
      setResultCount(result.count);
    } catch (error) {
      toast.error('Failed to test query');
    }
  };

  const handleAdd = async (mode: 'append' | 'overwrite') => {
    if (!isQueryValid) {
      setShowErrors(true);
      return;
    }
    try {
      const result = await addDatabaseItems({
        tableViewId,
        data: {
          source: source.id as DatabaseSource,
          query: currentQuery,
          mode,
        },
      });
      toast.success(
        `${mode === 'append' ? `Added ${result.addedCount} items` : `Replaced ${numItems} items from ${source.name} with ${result.addedCount} new items`}`
      );
    } catch (error) {
      toast.error('Failed to add or overwrite database items');
    }
  };

  const isLoading = isTesting || isAdding;

  // Get the appropriate input component for this database source
  const InputComponent = useMemo(
    () => getDatabaseInputComponent(source),
    [source]
  );

  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-6 flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-2.5">
        <FlexRow gap="2" alignItems="center" justifyContent="between">
          <FlexRow gap="2" alignItems="center">
            <DatabaseIcon sourceId={source.id} />
            <Text variant="body" className="font-medium">
              {source.name}
            </Text>
          </FlexRow>

          <Button
            variant="ghost"
            size="icon"
            onClick={handleToggleCollapse}
            disabled={isUpdating}
            className="h-8 w-8 text-neutral-500 hover:text-neutral-700"
          >
            {isUpdating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isCollapsed ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronUp className="w-4 h-4" />
            )}
          </Button>
        </FlexRow>
        <Text variant="body-sm" color="secondary">
          {source.description}
          {source.id === 'openalex' && (
            <>
              <br />
              <span className="italic">
                OpenAlex queries have a different format than PubMed queries, we
                recommend using the Qalico chat or another AI model to generate
                them.
              </span>
            </>
          )}
        </Text>
        {isRecommended && (
          <Badge
            variant="outline"
            className="w-fit border-none bg-yellow-50 !py-[3px] !px-2"
          >
            <FlexRow alignItems="center" gap="1.5">
              <Star className="w-4 h-4" />
              Recommended for template
            </FlexRow>
          </Badge>
        )}
      </div>
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[800px] opacity-100'
        }`}
      >
        {!isCollapsed && (
          <FlexCol>
            <FlexCol className="gap-2">
              <InputComponent
                initialQuery={initialQuery}
                onQueryChange={handleQueryChange}
                source={source}
                showErrors={showErrors}
                setShowErrors={setShowErrors}
              />
              <FlexRow justifyContent="end" alignItems="center" gap="2">
                <FlexRow alignItems="center" gap="1" p="2">
                  {resultCount !== null &&
                    (resultCount >= 1000 ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                            <Text variant="body-xs" color="secondary">
                              Query results in {resultCount.toLocaleString()}{' '}
                              results.
                            </Text>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[250px]">
                          Only the first 1000 results will be added to the
                          table.
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Text variant="body-xs" color="secondary">
                        Query results in {resultCount.toLocaleString()} database
                        items
                      </Text>
                    ))}
                </FlexRow>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleTest}
                        disabled={isLoading}
                      >
                        {isTesting ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                          </>
                        ) : (
                          <Text className="text-xs">Preview result count</Text>
                        )}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[250px]">
                    Test run the database query to preview how many database
                    items it will result in. No rows will be added to the table.
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block">
                      <Button
                        variant="dark"
                        size="sm"
                        onClick={() =>
                          doesTableHaveRows
                            ? setOpenConfirmationModal(true)
                            : handleAdd('append')
                        }
                        disabled={isLoading || resultCount === null}
                      >
                        {isAdding ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Adding...
                          </>
                        ) : (
                          <>
                            <Plus className="w-3 h-3" />
                            Add database items
                          </>
                        )}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[250px]">
                    Run the database query to add database items as rows in the
                    table
                  </TooltipContent>
                </Tooltip>
              </FlexRow>
            </FlexCol>
          </FlexCol>
        )}
      </div>
      <ConfirmationModal
        numItems={numItems}
        databaseSourceName={source.name}
        isOpen={openConfirmationModal}
        onOpenChange={setOpenConfirmationModal}
        onConfirm={handleAdd}
      />
    </div>
  );
};

interface DatabasePanelProps {
  tableViewId: string;
}

export const DatabasePanel: FC<DatabasePanelProps> = ({ tableViewId }) => {
  const { sources, isPending: isSourcesPending } = useDatabaseSourcesService();
  const { tableView, isPending: isTableViewPending } =
    useTableViewService(tableViewId);
  const { templates, isPending: isTemplatesPending } = useGetTemplatesService();
  const { savedQueries, isPending: isQueriesPending } =
    useDatabaseQueriesService(tableViewId);
  const isPending =
    isSourcesPending ||
    isQueriesPending ||
    isTemplatesPending ||
    isTableViewPending;

  const [searchSource, setSearchSource] = useState('');
  const [expandedSources, setExpandedSources] = useState<string[]>([]);

  // Get active databases from saved queries (where isActive is true)
  const activeDatabases = useMemo(
    () => new Set(savedQueries.filter(q => q.isActive).map(q => q.source)),
    [savedQueries]
  );

  // Classify sources based on template and active status
  const classifiedSources = useMemo(() => {
    const selectedTemplates = templates.filter(
      template => template.id === tableView?.templateId
    );

    // Get recommended database source IDs from selected templates
    const recommendedSourceIds = new Set(
      selectedTemplates.flatMap(template => template.databaseSources ?? [])
    );

    // Classify each source
    const templateAndActive: DatabaseSourceInfo[] = [];
    const notTemplateButActive: DatabaseSourceInfo[] = [];
    const notActive: DatabaseSourceInfo[] = [];

    sources.forEach(source => {
      const isRecommended = recommendedSourceIds.has(
        source.id as DatabaseSource
      );
      const isActive = activeDatabases.has(source.id);

      if (isRecommended && isActive) {
        templateAndActive.push(source);
      } else if (!isRecommended && isActive) {
        notTemplateButActive.push(source);
      } else {
        notActive.push(source);
      }
    });

    return {
      templateAndActive: templateAndActive.sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
      notTemplateButActive: notTemplateButActive.sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
      notActive: notActive.sort((a, b) => a.name.localeCompare(b.name)),
      recommendedSourceIds,
    };
  }, [templates, tableView, sources, activeDatabases]);

  // Combine all sources in priority order
  const displayedSources = useMemo(() => {
    const allSources = [
      ...classifiedSources.templateAndActive,
      ...classifiedSources.notTemplateButActive,
      ...classifiedSources.notActive,
    ];

    // Filter by search query
    if (searchSource.trim()) {
      const query = searchSource.trim().toLowerCase();
      return allSources.filter(
        source =>
          source.name.toLowerCase().includes(query) ||
          source.description.toLowerCase().includes(query)
      );
    }

    return allSources;
  }, [classifiedSources, searchSource]);

  // Sync expandedSources with activeDatabases when savedQueries load
  useEffect(() => {
    const activeDbArray = Array.from(activeDatabases);
    setExpandedSources(activeDbArray);
  }, [savedQueries]);

  const handleCollapse = (sourceId: string) => {
    setExpandedSources(prev =>
      prev.includes(sourceId)
        ? prev.filter(id => id !== sourceId)
        : [...prev, sourceId]
    );
  };

  // Create a map of source -> saved query for quick lookup
  const savedQueryMap = new Map(
    savedQueries.map(q => [
      q.source,
      { query: q.query, count: q.lastResultCount },
    ])
  );

  if (isPending) {
    return (
      <FlexCol className="flex-1 items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        <Text variant="body-sm" color="secondary" className="mt-2">
          Loading databases...
        </Text>
      </FlexCol>
    );
  }

  if (sources.length === 0) {
    return (
      <FlexCol className="flex-1 items-center justify-center px-4">
        <Database className="w-8 h-8 text-neutral-400" />
        <Text variant="body" className="font-medium mt-3">
          No databases available
        </Text>
        <Text
          variant="body-sm"
          color="secondary"
          className="text-center max-w-[290px]"
        >
          Contact support to request access to database integrations.
        </Text>
      </FlexCol>
    );
  }

  return (
    <FlexCol className="flex-1 overflow-hidden">
      {/* Search bar */}
      <div className="px-4 pt-4 pb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="Search databases..."
            value={searchSource}
            onChange={e => setSearchSource(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
      </div>

      {/* Database cards */}
      <FlexCol
        gap="4"
        className="flex-1 overflow-y-auto px-4 py-2 scrollbar-hide"
      >
        {displayedSources.length === 0 ? (
          <FlexCol className="flex-1 items-center justify-center py-12">
            <Database className="w-8 h-8 text-neutral-400" />
            <Text variant="body" className="font-medium mt-3">
              {searchSource.trim()
                ? 'No databases found'
                : 'No database sources available'}
            </Text>
            <Text
              variant="body-sm"
              color="secondary"
              className="text-center max-w-[290px]"
            >
              {searchSource.trim()
                ? 'Try adjusting your search query.'
                : 'Database sources will appear here.'}
            </Text>
          </FlexCol>
        ) : (
          displayedSources.map(source => {
            const saved = savedQueryMap.get(source.id);
            const isActive = activeDatabases.has(source.id);
            const isRecommended = classifiedSources.recommendedSourceIds.has(
              source.id as DatabaseSource
            );
            const numItems =
              tableView?.rows?.filter(row => row.source === source.id).length ??
              0;
            const hasSourceRows = numItems > 0;

            return (
              <DatabaseCard
                key={source.id}
                source={source}
                tableViewId={tableViewId}
                initialQuery={saved?.query}
                initialResultCount={saved?.count}
                isCollapsed={!expandedSources.includes(source.id)}
                onToggleCollapse={() => handleCollapse(source.id)}
                isRecommended={isRecommended}
                isActive={isActive}
                doesTableHaveRows={hasSourceRows}
                numItems={numItems}
              />
            );
          })
        )}
      </FlexCol>
    </FlexCol>
  );
};
